import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { usePageTitle } from "@/hooks/use-page-title";
import { PlanCalculator } from "@/components/pricing/plan-calculator";
import {
  formatUsd,
  quoteForSelection,
  type PlanSelection,
} from "@/components/pricing/plan-selection";
import type { AccountKind } from "@shared/modular-pricing";
import { useV3Billing } from "@/hooks/use-v3-billing";

interface AccountPlanResponse {
  accountKind: AccountKind;
  planTier: "essentials" | "pro";
  modules: string[];
  revenueAddon: boolean;
  researchAddon: boolean;
  cpmListing: boolean;
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  portalOwners: number;
  waivedOwners: number;
  offMarketplaceVendors: number;
  quote: { totalCents: number };
  hasSubscription: boolean;
  billingActive: boolean;
}

/**
 * The in-app plan editor — the same calculator the pricing page uses, in
 * account mode: room/owner/vendor counts come from the live account (read
 * only — they bill from real state), and submitting either applies the
 * change to the live subscription (with a prorated-diff confirm dialog) or
 * routes a not-yet-subscribed account through checkout.
 */
export default function AppAccountPlan() {
  usePageTitle("Plan & Modules");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // v3 per-property accounts have every feature included — no module
  // calculator, no "Coming …" module labels, no plan editor.
  const v3 = useV3Billing();

  const planQuery = useQuery<AccountPlanResponse>({
    queryKey: ["/api/app/account/plan"],
    retry: false,
    enabled: !v3,
  });

  const [pendingSelection, setPendingSelection] = useState<PlanSelection | null>(null);

  // Shape-checked: legacy accounts 409 here, and test stubs may answer with
  // {} — only a real modular payload drives the calculator.
  const server = planQuery.data?.accountKind ? planQuery.data : undefined;
  const initialSelection = useMemo<Partial<PlanSelection>>(
    () =>
      server
        ? {
            accountKind: server.accountKind,
            planTier: server.planTier,
            modules: server.modules as PlanSelection["modules"],
            revenueAddon: server.revenueAddon,
            researchAddon: server.researchAddon,
            cpmListing: server.cpmListing,
            roomsOnPadsplit: server.roomsOnPadsplit,
            roomsOffPadsplit: server.roomsOffPadsplit,
            ownersCount: server.portalOwners,
            offMarketplaceVendors: server.offMarketplaceVendors,
          }
        : {},
    [server],
  );

  const applyMutation = useMutation({
    mutationFn: async (selection: PlanSelection) => {
      const res = await apiRequest("PUT", "/api/app/account/plan", {
        planTier: selection.planTier,
        modules: selection.planTier === "essentials" ? selection.modules : [],
        ...(selection.accountKind === "operator"
          ? { revenueAddon: selection.revenueAddon, researchAddon: selection.researchAddon }
          : { cpmListing: selection.cpmListing }),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/plan"] });
      setPendingSelection(null);
      toast({ title: "Plan updated", description: "Your changes are live — mid-cycle changes prorate automatically." });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not update your plan",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (selection: PlanSelection) => {
    if (!server) return;
    if (!server.hasSubscription) {
      // Not subscribed yet (still in trial without a card) — the onboarding
      // payment step owns checkout.
      navigate("/app/onboarding/payment");
      return;
    }
    setPendingSelection(selection);
  };

  const pendingQuote = pendingSelection
    ? quoteForSelection(pendingSelection, server ? { portalOwners: server.portalOwners, waivedOwners: server.waivedOwners } : undefined)
    : null;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-tour="account-plan-heading" data-testid="text-account-plan-heading">
              Plan & Modules
            </h1>
            <p className="text-sm text-muted-foreground">
              Adjust your base plan and modules — changes apply to your subscription with automatic proration.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="account-plan" />
            <GuideButton pageKey="account-plan" />
          </div>
        </div>

        <div data-tour="account-plan-calculator">
        {v3 ? (
          <div className="rounded-lg border bg-background p-5 text-sm space-y-2" data-testid="card-plan-v3-included">
            <p className="font-medium">Every feature is included in your plan.</p>
            <p className="text-muted-foreground">
              PadPulse is billed per active property, so there are no modules to add or remove. See your
              properties, owners, locks and monthly total on{" "}
              <Link href="/app/account/billing" className="underline font-medium">
                billing
              </Link>
              .
            </p>
          </div>
        ) : planQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-brand" />
          </div>
        ) : planQuery.isError || !server ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="error-plan-not-modular">
            This account isn't on modular pricing yet. Manage your subscription from{" "}
            <Link href="/app/account/billing" className="underline font-medium">
              billing settings
            </Link>
            .
          </div>
        ) : (
          <PlanCalculator
              mode="account"
              initialSelection={initialSelection}
              realOwnerData={{ portalOwners: server.portalOwners, waivedOwners: server.waivedOwners }}
              ctaLabel={server.hasSubscription ? "Review & apply changes" : "Continue to checkout"}
              ctaPending={applyMutation.isPending}
              onSubmit={handleSubmit}
              footnote={
                <p className="text-xs text-muted-foreground text-center">
                  Room, owner, and private-vendor counts come from your live account — billing always
                  follows the real numbers.
                </p>
              }
            />
        )}
        </div>
      </div>

      <Dialog
        open={pendingSelection !== null}
        onOpenChange={(next) => {
          if (applyMutation.isPending) return;
          if (!next) setPendingSelection(null);
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="dialog-confirm-plan-change"
          onInteractOutside={(e) => {
            if (applyMutation.isPending) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (applyMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (applyMutation.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Apply plan changes?</DialogTitle>
            <DialogDescription>
              Your subscription updates immediately; Stripe prorates the difference for the current cycle.
            </DialogDescription>
          </DialogHeader>
          {pendingQuote && server && (
            <div className="space-y-2 text-sm" data-testid="text-plan-change-diff">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current monthly total</span>
                <span className="font-medium">{formatUsd(server.quote.totalCents)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">New monthly total</span>
                <span className="font-bold">{formatUsd(pendingQuote.totalCents)}</span>
              </div>
            </div>
          )}
          <Button
            className="w-full bg-brand hover:bg-brand/90 text-white font-semibold"
            disabled={applyMutation.isPending}
            onClick={() => pendingSelection && applyMutation.mutate(pendingSelection)}
            data-testid="button-confirm-plan-change"
          >
            {applyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {applyMutation.isPending ? "Applying…" : "Apply changes"}
          </Button>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
